import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import {
  deleteHealthReading,
  getBinahSetting,
  getHealthReading,
  getHealthReadingById,
  getReScanConfiguration,
  getScanParameterHistory,
  postBinahSetting,
  postScanSession,
} from "./api";
import {
  BinahSetting,
  HealthReadingDataResponse,
  InititaQRSession,
  ParameterHistory,
  ParameterHistoryParams,
  ReScanConfig,
  ScanReadings,
} from "./type";

type GetHealthReadingProps = Omit<
  UseQueryOptions<
    { data: HealthReadingDataResponse },
    Error,
    { data: HealthReadingDataResponse }
  >,
  "queryKey" | "queryFn" | "select"
> & {
  profileId: string;
};
export const useGetHealthReading = (props: GetHealthReadingProps) => {
  const { profileId, ...rest } = props;
  return useQuery({
    queryKey: ["health-reading", profileId],
    queryFn: () => getHealthReading(),
    select: ({ data }) => data,
    enabled: !!profileId,
    ...rest,
  });
};

export const useGetInfiniteHealthReading = ({
  profileId,
  initialPageParam,
}: {
  profileId: string;
  initialPageParam: number;
}) => {
  return useInfiniteQuery({
    queryKey: ["inifinite-health-reading", profileId],
    queryFn: async ({ pageParam }) => {
      const response = await getHealthReading({
        page_number: pageParam,
        page_size: 10,
      });

      return response.data;
    },
    initialPageParam,
    getNextPageParam: (lastPage, allPages) => {
      const allData = allPages.flatMap((page) => page.reading_data);
      if (allData.length >= lastPage.count) return undefined;

      return allPages.length + 1;
    },
    select: ({ pages }) => pages.flatMap((page) => page.reading_data),
    gcTime: 0,
    staleTime: 0,
  });
};

type DeleteHealthReadingArgs = Omit<
  UseMutationOptions<
    { success: boolean },
    Error,
    { reading_id: string[] },
    unknown
  >,
  "mutationFn"
>;
export const useDeleteHealthReading = (props?: DeleteHealthReadingArgs) => {
  return useMutation({
    mutationFn: (data: { reading_id: string[] }) => deleteHealthReading(data),
    ...props,
  });
};

type ReScanConfigurationArgs = Omit<
  UseQueryOptions<ReScanConfig, Error, ReScanConfig>,
  "queryKey" | "queryFn"
> & {
  profileId: string;
};
export const useGetReScanConfiguration = ({
  profileId,
  ...rest
}: ReScanConfigurationArgs) => {
  return useQuery({
    queryKey: ["rescan-configuration", profileId],
    queryFn: getReScanConfiguration,
    ...rest,
  });
};

type ScanParameterArgs = Omit<
  UseQueryOptions<
    Record<string, ParameterHistory[]>,
    Error,
    Record<string, ParameterHistory[]>
  >,
  "queryKey" | "queryFn"
> & {
  profileId: string;
  params?: ParameterHistoryParams;
};
export const useGetScanParameterHistory = (args: ScanParameterArgs) => {
  const { profileId, params, ...rest } = args;
  return useQuery({
    queryKey: ["parameter-history", profileId, params],
    queryFn: () => getScanParameterHistory(params),
    ...rest,
  });
};

type GetBinahSettingsArgs = Omit<
  UseQueryOptions<unknown, Error, unknown>,
  "queryKey" | "queryFn"
> & {
  profileId: string;
};
export const useGetBinahSettings = ({
  profileId,
  ...args
}: GetBinahSettingsArgs) => {
  return useQuery({
    queryKey: ["binah-setting", profileId],
    queryFn: getBinahSetting,
    staleTime: Infinity,
    gcTime: Infinity,
    ...args,
  }) as UseQueryResult<BinahSetting[]>;
};

export const useMutateBinahSettings = (
  args?: UseMutationOptions<BinahSetting[], Error, void>
) => {
  return useMutation({
    mutationKey: ["binah-setting"],
    mutationFn: async () => {
      const kp = await crypto.subtle.generateKey(
        {
          name: "RSA-OAEP",
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: "SHA-256",
        },
        true,
        ["encrypt", "decrypt"]
      );

      const spki = await crypto.subtle.exportKey("spki", kp.publicKey);
      const b64 = btoa(String.fromCharCode(...new Uint8Array(spki)));
      const pem = `-----BEGIN PUBLIC KEY-----${b64?.match(
        /.{1,64}/g
      )}-----END PUBLIC KEY-----`;

      const response = await postBinahSetting(pem);

      const encryptedItem = response.find(
        (item) => item.setting_name === "sdk_key"
      );

      if (!encryptedItem) {
        throw new Error("Encrypted SDK key not found in response");
      }

      const wrappedBuf = Uint8Array.from(
        atob(encryptedItem.setting_value),
        (c) => c.charCodeAt(0)
      ).buffer;

      const plainBuf = await crypto.subtle.decrypt(
        { name: "RSA-OAEP" },
        kp.privateKey,
        wrappedBuf
      );

      const decryptedKey = new TextDecoder().decode(plainBuf);

      const finalData = response.map((item) =>
        item.setting_name === "sdk_key"
          ? { ...item, setting_value: decryptedKey }
          : item
      );

      return finalData;
    },
    ...args,
  });
};

type GetHealthReadingByIdArgs = Omit<
  UseQueryOptions<{ data: ScanReadings }, Error, { data: ScanReadings }>,
  "queryKey" | "queryFn" | "select"
> & {
  readingId: string;
};
export const useGetHealthReadingById = (args: GetHealthReadingByIdArgs) => {
  const { readingId, ...rest } = args;
  return useQuery({
    queryKey: ["report-reading-by-id", readingId],
    queryFn: async () => await getHealthReadingById(readingId),
    enabled: !!readingId && readingId !== "None",
    select: ({ data }) => data,
    staleTime: Infinity,
    gcTime: Infinity,
    ...rest,
  });
};

export const usePostScanSession = (
  args?: Omit<
    UseMutationOptions<InititaQRSession, Error, unknown>,
    "mutationFn"
  >
) => {
  return useMutation({
    mutationFn: postScanSession,
    ...args,
  });
};
